import { useEffect, useState } from "react";
/**
 * Debounces a value for `timeout` milliseconds.
 *
 * This means that a rapidly changing value will only trigger a new return value from this hook when the value has settled for `timeout` milliseconds.
 *
 * This is useful for input fields used to trigger requests (e.g. search query field), or rapidly changing values like scroll position, mouse position, etc. used to trigger expensive handlers.
 *
 * > Note: When `value` is false-like, it is updated immediately.
 *
 * @param value The rapidly changing value
 * @param timeout Milliseconds to wait before returning an updated value
 * @returns `value`, when stable for at least `timeout` milliseconds.
 */
export default function useDebounce(value, timeout) {
    const [debounced, setDebounced] = useState(value);
    useEffect(() => {
        if (!value) {
            setDebounced(value);
            return;
        }
        const tId = setTimeout(() => setDebounced(value), timeout);
        return () => clearTimeout(tId);
    }, [value, timeout]);
    return debounced;
}
